/**
 * Internal conversion utilities
 *
 * Converts between human-readable values and contract scale.
 */
import { PRICE_DECIMALS, SHARE_DECIMALS, USDC_DECIMALS } from "../types"

// =============================================================================
// Constants
// =============================================================================

const USDC_SCALE = 10 ** USDC_DECIMALS // 1e6
const SHARE_SCALE = 10 ** SHARE_DECIMALS // 1e6
const PRICE_SCALE = 10 ** PRICE_DECIMALS // 1e18

/** Default slippage tolerance: 3% (300 basis points) */
export const DEFAULT_SLIPPAGE_BPS = 300

/** Minimum slippage: 0 bps (0%) */
export const MIN_SLIPPAGE_BPS = 0

/** Maximum slippage: 5000 bps (50%) - prevent unreasonable values */
export const MAX_SLIPPAGE_BPS = 5000

/**
 * Validate slippage is within acceptable bounds
 * @param slippageBps Slippage in basis points
 * @throws Error if slippage is invalid
 */
export function validateSlippageBps(slippageBps: number): void {
	if (!Number.isFinite(slippageBps)) {
		throw new Error("Slippage must be a finite number")
	}
	if (!Number.isInteger(slippageBps)) {
		throw new Error("Slippage must be an integer (basis points)")
	}
	if (slippageBps < MIN_SLIPPAGE_BPS || slippageBps > MAX_SLIPPAGE_BPS) {
		throw new Error(`Slippage must be between ${MIN_SLIPPAGE_BPS} and ${MAX_SLIPPAGE_BPS} bps`)
	}
}

/** Default deadline: 5 minutes (300 seconds) */
export const DEFAULT_DEADLINE_SECONDS = 300

/** Basis points denominator (10000 = 100%) */
export const BPS_DENOMINATOR = 10_000

// =============================================================================
// To Contract Scale (human -> bigint)
// =============================================================================

/**
 * Convert human-readable USDC amount to contract scale
 * Uses string-based conversion to avoid floating point precision loss.
 * @example toUsdcScale(100.50) => 100500000n
 * @example toUsdcScale(100.005) => 100005000n (not 100004999n)
 */
export function toUsdcScale(amount: number): bigint {
	return toScale(amount, USDC_DECIMALS)
}

/**
 * Convert human-readable share amount to contract scale
 * Uses string-based conversion to avoid floating point precision loss.
 * @example toShareScale(100) => 100000000n
 */
export function toShareScale(amount: number): bigint {
	return toScale(amount, SHARE_DECIMALS)
}

/**
 * Convert a number to scaled bigint without floating point precision loss.
 * Handles the conversion via string manipulation.
 */
function toScale(amount: number, decimals: number): bigint {
	if (amount < 0) {
		throw new Error("Amount must be non-negative")
	}

	// Convert to string and handle scientific notation
	const str = amount.toFixed(decimals + 1) // Extra digit for proper rounding
	const [wholePart, fracPart = ""] = str.split(".")

	// Take only the required decimals (truncate, don't round)
	const truncatedFrac = fracPart.slice(0, decimals).padEnd(decimals, "0")

	// Combine and convert to BigInt
	const combined = wholePart + truncatedFrac
	return BigInt(combined)
}

/**
 * Convert Date to Unix timestamp (seconds)
 * @example toTimestamp(new Date()) => 1234567890n
 */
export function toTimestamp(date: Date): bigint {
	return BigInt(Math.floor(date.getTime() / 1000))
}

/**
 * Create deadline timestamp from seconds from now
 * @example createDeadline(300) => timestamp 5 minutes from now
 */
export function createDeadline(secondsFromNow: number): bigint {
	return BigInt(Math.floor(Date.now() / 1000) + secondsFromNow)
}

/**
 * Create deadline Date from seconds from now
 */
export function createDeadlineDate(secondsFromNow: number): Date {
	return new Date(Date.now() + secondsFromNow * 1000)
}

// =============================================================================
// From Contract Scale (bigint -> human)
// =============================================================================

/**
 * Convert contract scale USDC to human-readable
 * @example fromUsdcScale(100500000n) => 100.50
 */
export function fromUsdcScale(amount: bigint): number {
	return Number(amount) / USDC_SCALE
}

/**
 * Convert contract scale shares to human-readable
 * @example fromShareScale(100000000n) => 100
 */
export function fromShareScale(amount: bigint): number {
	return Number(amount) / SHARE_SCALE
}

/**
 * Convert contract scale price (WAD) to 0-1 range
 * @example fromPriceScale(500000000000000000n) => 0.50
 */
export function fromPriceScale(price: bigint): number {
	return Number(price) / PRICE_SCALE
}

/**
 * Convert Unix timestamp to Date
 * @example fromTimestamp(1234567890n) => Date
 */
export function fromTimestamp(timestamp: bigint): Date {
	return new Date(Number(timestamp) * 1000)
}

// =============================================================================
// Array Conversions
// =============================================================================

/**
 * Convert array of contract scale prices to human-readable
 */
export function fromPriceScaleArray(prices: readonly bigint[]): number[] {
	return prices.map(fromPriceScale)
}

/**
 * Convert array of contract scale shares to human-readable
 */
export function fromShareScaleArray(shares: readonly bigint[]): number[] {
	return shares.map(fromShareScale)
}

// =============================================================================
// Slippage Calculations
// =============================================================================

/**
 * Apply slippage to max cost (increase)
 * @param cost Base cost in human-readable USDC
 * @param slippageBps Slippage in basis points (200 = 2%)
 * @returns Max cost with slippage applied
 */
export function applyBuySlippage(cost: number, slippageBps: number): number {
	return cost * (1 + slippageBps / 10000)
}

/**
 * Apply slippage to max cost in contract scale
 */
export function applyBuySlippageScale(cost: bigint, slippageBps: number): bigint {
	return (cost * BigInt(10000 + slippageBps)) / 10000n
}

/**
 * Apply slippage to min proceeds (decrease)
 * @param proceeds Base proceeds in human-readable USDC
 * @param slippageBps Slippage in basis points (200 = 2%)
 * @returns Min proceeds with slippage applied
 */
export function applySellSlippage(proceeds: number, slippageBps: number): number {
	return proceeds * (1 - slippageBps / 10000)
}

/**
 * Apply slippage to min proceeds in contract scale
 */
export function applySellSlippageScale(proceeds: bigint, slippageBps: number): bigint {
	return (proceeds * BigInt(10000 - slippageBps)) / 10000n
}
